import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { HelperText, RadioButton } from 'react-native-paper';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { usePreferencesStore } from 'src/domain/stores/usePreferencesStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import { localeFlag } from 'src/core/utils/Locales';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

export default function LanguagePreferencesScreen() {
	const { language, setLanguage, user } = useAuthStore();
	const { preferredLanguages, isLoading, errorMessage, getApiPreferences, putApiPreferredLanguage } =
		usePreferencesStore();
	const { logEvent, captureEvent } = useServicesStore();
	const tabBarInset = useTabBarInset();

	useAppStateScreenListener({
		onInit: () => getApiPreferences(),
	});

	const selectedLanguageId = preferredLanguages?.find((language) => language.selected)?.id ?? language;

	const saveLanguagePreference = async (lang: string) => {
		await putApiPreferredLanguage({
			id: lang,
			onSuccess: async () => {
				await setLanguage(lang);

				logEvent({
					name: Constants.analyticsEventChangeAccountSettings,
					params: {
						[Constants.analyticsParamScreenPage]: Constants.screenLanguagePreferences,
						[Constants.analyticsParamSelectItem]: Constants.analyticsValueSelectItemLanguagePrefix + lang,
					},
					user,
				});
			},
			onError: () => getApiPreferences(),
		});
	};

	return (
		<View style={Styles.screen.white}>
			{isLoading ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
					<HelperText type="error" visible={errorMessage !== null}>
						{errorMessage}
					</HelperText>
					{preferredLanguages &&
						Object.entries(preferredLanguages).map(([key, language]) => {
							return (
								<TouchableOpacity
									key={key}
									onPress={() =>
										saveLanguagePreference(language.id).catch((error: ApiRequestError) =>
											captureEvent({ message: 'saveLanguagePreference failed', extra: { error } }),
										)
									}
								>
									<View style={[styles.menuitem, styles.borderBottom]}>
										<View style={styles.languageRow}>
											<ThermenImage source={localeFlag(language.id)} style={styles.flag} />
											<Text ellipsizeMode="head" numberOfLines={1} style={styles.languageName}>
												{language.name}
											</Text>
											<View style={styles.checkboxAlign}>
												<RadioButton.Android
													status={selectedLanguageId === language.id ? Constants.radioStatusChecked : Constants.radioStatusUnchecked}
													value={language.id}
													onPress={() => {
														saveLanguagePreference(language.id).catch((error: ApiRequestError) =>
															captureEvent({ message: 'saveLanguagePreference failed', extra: { error } }),
														);
													}}
												/>
											</View>
										</View>
									</View>
								</TouchableOpacity>
							);
						})}
				</ScrollView>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	menuitem: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding20,
		marginHorizontal: Dimens.padding18,
	},
	borderBottom: {
		borderColor: Colors.gray200,
		borderBottomWidth: Dimens.borderWidth1,
	},
	checkboxAlign: {
		flex: Dimens.flex1,
		alignItems: 'flex-end',
	},
	languageRow: {
		flexDirection: 'row',
		alignItems: 'center',
		width: '100%',
	},
	languageName: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
		fontFamily: Fonts.barlowMedium,
	},
	flag: {
		width: Dimens.size16,
		height: Dimens.size12,
		marginRight: Dimens.padding16,
	},
});
